import React, {useEffect, useState} from "react";
import {Box, Stack, Typography} from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import dayjs from "dayjs";
import {ManagementRoleAssignment, PersonManagementRoleRestControllerApiFactory} from "arteco-api-client-ts";
import {useApiManager} from "../../../../components/utils/api";
import {dateToString} from "../../../../components/utils/dates";
import {fullName, Panel, SkeletonRows} from "../../../../components/kit";
import {MANAGEMENT_ROLES, PersonRole, RoleBadges, managementRoleLabel} from "../../../../components/layout/shared/management_roles";

const isCurrent = (a: ManagementRoleAssignment) => {
	const t = dayjs().format("YYYY-MM-DD");
	return a.startDate <= t && (!a.endDate || a.endDate >= t);
};

/**
 * Roles del sistema de gestión de una persona en su ficha (#13896): los vigentes como distintivos y
 * los que ha tenido. Solo lectura: se designan en Configuración › Roles del sistema de gestión.
 */
export const PersonRolesPanel = ({personId}: { personId?: number }) => {
	const apiManager = useApiManager();
	const [list, setList] = useState<ManagementRoleAssignment[]>();

	useEffect(() => {
		if (!personId) {
			return;
		}
		apiManager.execute(apiManager.factory(PersonManagementRoleRestControllerApiFactory).listByPerson(personId))
			.then(setList).catch(() => setList([]));
	}, [personId]);

	if (!personId) {
		return null;
	}
	const current: PersonRole[] = (list || []).filter(isCurrent).map(a => ({
		role: a.role, deputy: a.holder?.id !== personId, acting: a.holder?.id !== personId && !a.holder,
	})).sort((x, y) => MANAGEMENT_ROLES.findIndex(r => r.value === x.role) - MANAGEMENT_ROLES.findIndex(r => r.value === y.role));
	const past = (list || []).filter(a => !isCurrent(a));

	return (
		<Panel title="Roles del sistema de gestión" icon={<VerifiedUserOutlinedIcon fontSize="small" color="action"/>}
			   subtitle="Designaciones de ISO 9001, ISO/IEC 27001 y ENS. Se cambian en Configuración." sx={{mb: 3}}>
			{!list ? <SkeletonRows rows={1} height={32}/> : list.length === 0 ? (
				<Typography variant="body2" color="text.secondary">No tiene ni ha tenido ningún rol designado.</Typography>
			) : (
				<Stack spacing={2}>
					{current.length > 0
						? <RoleBadges roles={current}/>
						: <Typography variant="body2" color="text.secondary">Hoy no tiene ningún rol.</Typography>}
					{past.length > 0 && (
						<Box>
							<Typography variant="caption" color="text.secondary" display="block" sx={{mb: 0.5}}>Ha tenido</Typography>
							<Stack spacing={0.5}>
								{past.map(a => (
									<Typography key={a.id} variant="body2">
										{managementRoleLabel(a.role)}{a.holder?.id !== personId ? " (suplente" + (a.holder ? ` de ${fullName(a.holder)})` : ", en funciones)") : ""}
										<Typography component="span" variant="body2" color="text.secondary">
											{" · "}{a.endDate ? `del ${dateToString(a.startDate)} al ${dateToString(a.endDate)}` : `desde el ${dateToString(a.startDate)}`}
										</Typography>
									</Typography>
								))}
							</Stack>
						</Box>
					)}
				</Stack>
			)}
		</Panel>
	);
};
